import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import {
  SectionCard, EmptyState, ActionButton, LoadingState, Pill, StatusBadge,
  TimelineItem, DataTable,
} from "@/components/shared";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import WorkspaceNav from "./workspace/WorkspaceNav";
import PortalAdminCard from "@/components/portal/PortalAdminCard";
import TaxProfileSummaryCard from "@/components/TaxProfileSummaryCard";
import { HistoricalSummaryCard } from "@/components/HistoricalSummaryCard";
import StaffContactEditor from "@/components/StaffContactEditor";
import { CitizenshipBadges } from "@/components/CitizenshipSelect";
import { PhoneCallMenu } from "@/components/PhoneCallMenu";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PortalStatusIndicator } from "@/components/PortalStatusIndicator";
import LeadDeletionModal from "./LeadDeletionModal";
import { MergeContactsDialog } from "@/components/MergeContactsDialog";

const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const LIFECYCLE_TONES = { lead: "info", active_client: "success", inactive_client: "neutral", active: "success" };
const LIFECYCLE_LABELS = { lead: "Lead", active_client: "Active Client", inactive_client: "Inactive Client", active: "Active Client" };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

// System-of-Record navigation — Identity / Administration / Reference only.
// Anything operational deep-links into the Lead Workspace (system of work).
const CRM_NAV_GROUPS = [
  { label: "Identity", items: [
    ["overview", "Overview", "IdCard"], ["contact", "Contact", "User"],
    ["relationships", "Relationships", "Network"], ["tax", "Tax Profile", "FileUser"],
  ] },
  { label: "Administration", items: [
    ["assignment", "Assignment", "Users"], ["lifecycle", "Lifecycle", "GitBranch"],
    ["portal", "Portal", "Globe"], ["financial", "Financial", "Receipt"],
  ] },
  { label: "Reference", items: [
    ["timeline", "Timeline", "Activity"], ["notes", "Notes", "StickyNote"],
  ] },
];

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-50 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm text-slate-800 font-medium text-right">{value || "—"}</span>
    </div>
  );
}

// A read-only operational summary row that launches the relevant Workspace section.
function OpRow({ icon, label, value, onOpen, testid }) {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <button onClick={onOpen} data-testid={testid}
      className="w-full flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0 text-left group rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
      <span className="flex items-center gap-2.5 text-sm text-slate-700"><Icon className="h-4 w-4 text-slate-400" />{label}</span>
      <span className="flex items-center gap-2 text-sm font-medium text-slate-800">{value}<Icons.ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-charcoal" /></span>
    </button>
  );
}

function MiniStat({ label, value, icon, tone = "slate" }) {
  const Icon = Icons[icon] || Icons.Circle;
  const tones = { slate: "text-slate-700", rose: "text-rose-600" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className={`text-2xl font-heading font-semibold mt-1 ${tones[tone]}`}>{value ?? 0}</div>
    </div>
  );
}

function ListCard({ title, icon, rows, columns, empty, money = [] }) {
  const cols = columns.map(([key, label]) => ({ key, label, align: money.includes(key) ? "right" : undefined }));
  const renderCell = (r, c) => {
    if (money.includes(c.key)) return usd(r[c.key]);
    if (c.key.endsWith("_at") || c.key === "created_at") return fmtDate(r[c.key]);
    if (c.key === "status") return <Pill tone="neutral" className="capitalize">{(r.status || "").replace(/_/g, " ")}</Pill>;
    return r[c.key] || "—";
  };
  return (
    <SectionCard title={title} icon={icon}>
      <DataTable columns={cols} rows={rows || []} renderCell={renderCell} empty={<EmptyState icon={icon} title={empty} />} />
    </SectionCard>
  );
}

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [related, setRelated] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [noteOpen, setNoteOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [advisories, setAdvisories] = useState([]);
  const [convertTarget, setConvertTarget] = useState(null); // null | "active_client" | "inactive_client"
  const [converting, setConverting] = useState(false);
  const [showLeadDelete, setShowLeadDelete] = useState(false);
  const [showInactiveConfirm, setShowInactiveConfirm] = useState(false);
  const [convertingInactive, setConvertingInactive] = useState(false);
  const [showMerge, setShowMerge] = useState(false);

  // Tombstone chain resolution: follow merged_into links to canonical survivor
  const [survivorInfo, setSurvivorInfo] = useState(null);
  const [survivorFetchError, setSurvivorFetchError] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get(`/crm/contacts/${id}`);
      const isMerged = d.data?.contact?.status === "merged";
      const [r, t] = await Promise.all([
        isMerged ? Promise.resolve({ data: null }) : api.get(`/crm/contacts/${id}/related`).catch(() => ({ data: null })),
        api.get("/timeline", { params: { entity_type: "client", entity_id: id, limit: 50 } }).catch(() => ({ data: [] })),
      ]);
      setData(d.data); setRelated(r.data); setTimeline(t.data || []);
    } catch (e) {
      toast.error("Could not load contact");
      navigate("/crm");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let ok = true;
    api.get(`/service-intelligence/clients/${id}/advisories`)
      .then((a) => { if (ok) setAdvisories(a.data?.advisories || []); })
      .catch(() => { if (ok) setAdvisories([]); });
    return () => { ok = false; };
  }, [id]);

  // Tombstone chain resolution: follow merged_into to the canonical survivor (max 5 hops, loop-safe)
  useEffect(() => {
    const c = data?.contact;
    if (!c || c.status !== "merged" || !c.merged_into) { setSurvivorInfo(null); setSurvivorFetchError(false); return; }
    let cancelled = false;
    const resolve = async () => {
      const seen = new Set([c.id]);
      let targetId = c.merged_into;
      for (let depth = 0; depth < 5; depth++) {
        if (!targetId || seen.has(targetId)) { if (!cancelled) setSurvivorFetchError(true); return; }
        seen.add(targetId);
        try {
          const { data: resp } = await api.get(`/crm/contacts/${targetId}`);
          const contact = resp?.contact || resp;
          if (contact.status !== "merged" || !contact.merged_into) {
            if (!cancelled) setSurvivorInfo({ id: contact.id, full_name: contact.full_name, client_number: contact.client_number });
            return;
          }
          targetId = contact.merged_into;
        } catch { if (!cancelled) setSurvivorFetchError(true); return; }
      }
      if (!cancelled) setSurvivorFetchError(true); // depth exceeded
    };
    resolve();
    return () => { cancelled = true; };
  }, [data?.contact?.id, data?.contact?.status, data?.contact?.merged_into]);

  // Deep-link into the Lead Workspace, preserving the intended section.
  const openWs = useCallback((wtab) => {
    try { if (wtab) localStorage.setItem(`tf.ws.lastTab.${id}`, wtab); } catch { /* ignore */ }
    navigate(`/client-workspace/${id}`);
  }, [id, navigate]);

  const convert = async (to) => {
    setConverting(true);
    try { await api.post(`/crm/contacts/${id}/convert`, { to }); toast.success("Lifecycle updated"); setConvertTarget(null); load(); }
    catch (e) { toast.error("Could not update lifecycle"); }
    finally { setConverting(false); }
  };

  const convertToInactiveClient = async () => {
    setConvertingInactive(true);
    try {
      await api.patch(`/crm/contacts/${id}`, { lifecycle_status: "inactive_client" });
      toast.success("Converted to Inactive Client");
      setShowInactiveConfirm(false);
      load();
    } catch (e) {
      toast.error("Could not update lifecycle");
    } finally {
      setConvertingInactive(false);
    }
  };

  const sendConsultInvite = async () => {
    try {
      const { data: r } = await api.post(`/crm/contacts/${id}/consult-invite`);
      toast.success(r.email_status === "disabled"
        ? `Consult invite prepared (${r.brand}) — email delivery is off`
        : `Branded ${r.brand} consult invite sent`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send consult invite"); }
  };

  const [portalChipBusy, setPortalChipBusy] = useState(false);
  const handlePortalChipAction = useCallback(async (status) => {
    if (status === "not_invited") {
      if (!data?.contact?.email) { toast.error("Add an email address before enabling portal access."); return; }
      setPortalChipBusy(true);
      try {
        const { data: r } = await api.post(`/portal/admin/${id}/enable`);
        toast.success(r.message || "Portal invitation sent");
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not send portal invitation"); }
      finally { setPortalChipBusy(false); }
    } else if (status === "invited") {
      setPortalChipBusy(true);
      try {
        const { data: r } = await api.post(`/portal/admin/${id}/resend`);
        toast.success(r.message || "Invitation resent");
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend invitation"); }
      finally { setPortalChipBusy(false); }
    } else {
      // active / disabled — navigate to Portal tab where full controls live
      setTab("portal");
    }
  }, [id, data, load, setTab]);
  if (!data) return null;

  const c = data.contact;
  const tax = data.tax_profile || {};
  const sum = data.summary || {};
  const wf = data.workflow;
  const stageLabel = wf?.pipeline?.stages?.find((s) => s.key === wf?.state?.current_stage)?.label || wf?.state?.current_stage || "—";
  const initials = (c.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const convCount = (related?.conversations || []).length;
  const docCount = sum.documents ?? (related?.documents || []).length;
  const engCount = (related?.engagements || []).length;
  const isTombstone = c.status === "merged";
  const canEditContact = !!user && !isTombstone && (
    (user.permissions || []).includes("*") ||
    (user.permission_keys || []).includes("crm.edit") ||
    ((user.modules || []).includes("crm") && user.can_write)
  );

  return (
    <div data-testid="contact-detail-page">
      {/* Tombstone banner — shown for merged/retired contacts */}
      {isTombstone && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-800 text-white px-4 py-3.5"
          data-testid="tombstone-banner">
          <Icons.GitMerge className="h-5 w-5 text-slate-300 shrink-0" />
          <div className="flex-1 min-w-0">
            {survivorInfo ? (
              <span className="text-sm">
                This contact was merged →{" "}
                <button
                  onClick={() => navigate(`/crm/${survivorInfo.id}`)}
                  data-testid="tombstone-survivor-link"
                  className="font-semibold underline underline-offset-2 hover:text-blue-300 transition-colors">
                  {survivorInfo.full_name} · {survivorInfo.client_number}
                </button>
              </span>
            ) : survivorFetchError ? (
              <span className="text-sm text-slate-300">
                This contact was merged into another contact.
                {c.merged_into && (
                  <span className="ml-2 font-mono text-xs text-slate-400" data-testid="tombstone-survivor-uuid">
                    Survivor UUID: {c.merged_into}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-sm text-slate-300 flex items-center gap-2">
                <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />
                This contact was merged — resolving canonical survivor…
              </span>
            )}
          </div>
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider bg-slate-700 text-slate-300 rounded-full px-2.5 py-1">
            Retired record
          </span>
        </div>
      )}
      {c?.contact_info_status === "incomplete" && !c?.email && !(c?.mobile_phone || c?.phone || c?.mobile || c?.home_phone) && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3" data-testid="contact-info-required-warning">
          <Icons.MailWarning className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-orange-900">Contact information required</div>
            <div className="text-sm text-orange-800 mt-0.5">Lead created from a meeting; email and phone are not yet available. Please complete the contact details.</div>
          </div>
        </div>
      )}
      {advisories.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="citizenship-advisories">
          {advisories.map((a) => (
            <div key={a.code} data-testid={`advisory-${a.code}`}
              className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <Icons.ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-amber-900">{a.title}
                  <span className="ml-2 align-middle text-[10px] font-medium uppercase tracking-wide text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">Advisory</span>
                </div>
                <div className="text-sm text-amber-800 mt-0.5">{a.message}</div>
                {(a.evidence || []).map((e, i) => (
                  <div key={i} className="text-xs text-amber-700/80 mt-1">{e.label}{e.detail ? `: ${e.detail}` : ""}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Header — system-of-record identity + launch point */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-5 sticky top-0 z-10" data-testid="contact-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/crm")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" aria-label="Back to CRM" data-testid="contact-back"><Icons.ArrowLeft className="h-4 w-4" /></button>
            <div className="h-14 w-14 rounded-2xl bg-charcoal text-white flex items-center justify-center text-lg font-semibold">{initials}</div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="font-heading text-2xl font-semibold text-slate-900">{c.full_name || "Unnamed Contact"}</h1>
                <StatusBadge status={c.lifecycle_status} map={LIFECYCLE_TONES} />
                {!["lead", "active_client", "inactive_client"].includes(c.contact_type) && (
                  <Pill tone="neutral" className="capitalize">{(c.contact_type || "").replace(/_/g, " ")}</Pill>
                )}
              </div>
              <div className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                <span className="font-mono text-xs">{c.client_number}</span>
                {c.email && <span className="flex items-center gap-1"><Icons.Mail className="h-3.5 w-3.5" />{c.email}</span>}
                {c.country_of_residence && <span className="flex items-center gap-1"><Icons.MapPin className="h-3.5 w-3.5" />{c.country_of_residence}</span>}
                <span className="flex items-center gap-1"><Icons.Heart className="h-3.5 w-3.5 text-rose-400" />Health {c.health_score ?? 100}</span>
                <PortalStatusIndicator client={c} onPortalAction={handlePortalChipAction} busy={portalChipBusy} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isTombstone && (
              <>
                <ActionButton icon="LayoutDashboard" onClick={() => openWs()} data-testid="contact-open-workspace">Open Workspace</ActionButton>
                <ActionButton icon="Globe" variant="secondary" onClick={() => setTab("portal")} data-testid="contact-view-portal">View Portal</ActionButton>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-charcoal px-4 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-testid="contact-quick-actions">
                      <Icons.Zap className="h-4 w-4" /> Quick Actions <Icons.ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => setNoteOpen(true)} data-testid="qa-add-note"><Icons.StickyNote className="h-4 w-4 mr-2" />Add Note</DropdownMenuItem>
                    <DropdownMenuItem onClick={sendConsultInvite} data-testid="qa-consult-invite"><Icons.MailCheck className="h-4 w-4 mr-2" />Send Consult Invite</DropdownMenuItem>
                    {c.lifecycle_status === "lead" && (
                      <DropdownMenuItem onClick={() => setShowInactiveConfirm(true)} data-testid="qa-convert-inactive">
                        <Icons.UserMinus className="h-4 w-4 mr-2 text-slate-500" />Convert to Inactive Client
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setTab("lifecycle")} data-testid="qa-lifecycle"><Icons.GitBranch className="h-4 w-4 mr-2" />Manage Lifecycle</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openWs()} data-testid="qa-open-workspace"><Icons.LayoutDashboard className="h-4 w-4 mr-2" />Open Client Workspace</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowMerge(true)} data-testid="qa-merge-contact">
                      <Icons.GitMerge className="h-4 w-4 mr-2 text-slate-500" />Merge with another contact...
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Grouped left nav (Staff Design System) + record content */}
      <div className="flex gap-5 items-start">
        <WorkspaceNav tab={tab} setTab={setTab} groups={CRM_NAV_GROUPS} idPrefix="crm"
          navTestId="crm-nav" selectTestId="crm-nav-select" storageKey="tf.crm.navCollapsed" />
        <div className="flex-1 min-w-0">
          <Tabs value={tab} onValueChange={setTab}>
            {/* OVERVIEW — identity-centric */}
            <TabsContent value="overview">
              {/* Allison Snapshot — read-only pointer into the Workspace */}
              <div className="rounded-xl border border-slate-200 border-l-4 border-l-brand-gold bg-white shadow-sm p-4 mb-5" data-testid="crm-allison-snapshot">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2.5">
                    <span className="h-9 w-9 rounded-lg bg-gradient-to-br from-slate-700 to-charcoal flex items-center justify-center"><Icons.Sparkles className="h-4 w-4 text-brand-gold" /></span>
                    <div>
                      <div className="text-sm font-heading font-semibold text-slate-900">Allison Snapshot</div>
                      <div className="text-[11px] text-slate-400">Current stage: <span className="capitalize">{stageLabel}</span></div>
                    </div>
                  </div>
                  <button onClick={() => openWs()} data-testid="crm-allison-open"
                    className="text-sm font-medium text-charcoal hover:text-graphite flex items-center gap-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
                    Open in Workspace <Icons.ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Allison's live recommendation, briefing, and approvals are available in the {["active_client", "inactive_client"].includes(c.lifecycle_status) ? "Client Workspace" : "Lead Workspace"}.</p>
              </div>

              {/* Historical Relationship Summary — only renders for finalized historical-import records */}
              <HistoricalSummaryCard clientId={c.id} />

              {/* Row 1 — Identity */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <SectionCard title="Contact" icon="User" action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("contact")} data-testid="ov-view-contact">Details</ActionButton>}>
                  <Row label="Email" value={c.email} />
                  <Row label="Mobile Phone" value={c.mobile_phone} />
                  <Row label="Home Phone" value={c.home_phone} />
                  <Row label="WhatsApp" value={c.whatsapp} />
                  <Row label="Current Residence" value={c.country_of_residence} />
                  {c.planned_country_of_residence && (
                    <Row label="Planned / Intended" value={c.planned_country_of_residence} />
                  )}
                  <Row label="Language" value={c.preferred_language} />
                </SectionCard>
                <SectionCard title="Assignment" icon="Users" action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("assignment")} data-testid="ov-view-assignment">Manage</ActionButton>}>
                  <Row label="Assigned Team" value={c.assigned_team_name} />
                  <Row label="Assigned Individual" value={c.assigned_case_manager_name} />
                  <Row label="Priority" value={<span className="capitalize">{c.priority_level}</span>} />
                  <Row label="Lead Source" value={c.lead_source} />
                </SectionCard>
                <SectionCard title="Relationship Summary" icon="Network" action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("relationships")} data-testid="ov-view-relationships">Relationships</ActionButton>}>
                  <Row label="Spouse / Partner" value={c.spouse_name} />
                  <Row label="Dependents" value={tax.dependent_count ?? null} />
                  <Row label="Business" value={c.business_name} />
                  <Row label="Referred By" value={c.referral_source} />
                </SectionCard>
              </div>

              {/* Row 2 — Administration */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
                <SectionCard title="Administrative Snapshot" icon="ClipboardList">
                  <Row label="Lifecycle" value={LIFECYCLE_LABELS[c.lifecycle_status] || "Lead"} />
                  <Row label="Client Since" value={fmtDate(c.created_at)} />
                  <Row label="Client Rating" value={c.client_rating ? `${c.client_rating}/5` : null} />
                  <Row label="Estimated LTV" value={c.estimated_lifetime_value ? usd(c.estimated_lifetime_value) : null} />
                </SectionCard>
                <SectionCard title="Portal Status" icon="Globe" action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("portal")} data-testid="ov-view-portal">Manage</ActionButton>}>
                  <Row label="Status" value={<span className="capitalize">{(c.portal_status || "not invited").replace(/_/g, " ")}</span>} />
                  <Row label="Portal Email" value={c.portal_email || c.email} />
                  <Row label="Last Login" value={c.portal_last_login ? new Date(c.portal_last_login).toLocaleString() : "Never"} />
                </SectionCard>
                <SectionCard title="Financial Snapshot" icon="Receipt" action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("financial")} data-testid="ov-view-financial">Financial</ActionButton>}>
                  <Row label="Outstanding Balance" value={usd(sum.outstanding_balance)} />
                  <Row label="Invoices" value={(related?.invoices || []).length} />
                  <Row label="Payments" value={(related?.payments || []).length} />
                </SectionCard>
              </div>

              {/* Row 3 — Operational Snapshot (read-only; everything launches the Workspace) */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
                <div className="lg:col-span-2">
                  <SectionCard title="Current Work" icon="Briefcase"
                    action={<ActionButton icon="LayoutDashboard" variant="secondary" onClick={() => openWs()} data-testid="op-open-workspace">Open Workspace</ActionButton>}>
                    <div data-testid="crm-operational-snapshot">
                      <OpRow icon="GitMerge" label="Workflow" value={<span className="capitalize">{stageLabel}</span>} onOpen={() => openWs("workflow")} testid="op-workflow" />
                      <OpRow icon="MessageSquare" label="Communications" value={`${convCount} conversation${convCount === 1 ? "" : "s"}`} onOpen={() => openWs("communication")} testid="op-communications" />
                      <OpRow icon="FileText" label="Documents" value={docCount} onOpen={() => openWs("documents")} testid="op-documents" />
                      <OpRow icon="Briefcase" label="Engagements" value={engCount} onOpen={() => openWs("engagements")} testid="op-engagements" />
                      <OpRow icon="Calendar" label="Meetings" value="View" onOpen={() => openWs("meetings")} testid="op-meetings" />
                      <OpRow icon="CheckSquare" label="Tasks" value={`${sum.open_tasks || 0} open`} onOpen={() => openWs("tasks")} testid="op-tasks" />
                    </div>
                  </SectionCard>
                </div>
                <SectionCard title="Recent Activity" icon="Activity" action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("timeline")} data-testid="ov-view-timeline">Full timeline</ActionButton>}>
                  {timeline.length === 0 ? <EmptyState icon="Activity" title="No activity yet" /> : (
                    <div className="space-y-4">
                      {timeline.slice(0, 5).map((e, i, arr) => (
                        <TimelineItem key={e.id} icon={e.icon} title={e.title} description={e.description}
                          meta={`${e.actor_name} · ${new Date(e.created_at).toLocaleString()}`} last={i === arr.length - 1} />
                      ))}
                    </div>
                  )}
                </SectionCard>
              </div>
            </TabsContent>

            {/* CONTACT INFO — authoritative editing (System of Record) */}
            <TabsContent value="contact">
              <SectionCard title="Contact Information" icon="User"
                action={canEditContact && !editingContact ? (
                  <ActionButton variant="ghost" icon="Pencil" onClick={() => setEditingContact(true)} data-testid="crm-contact-edit-btn">Edit</ActionButton>
                ) : null}>
                {editingContact ? (
                  <StaffContactEditor contact={c} clientId={id} testidPrefix="crm-contact-edit"
                    onCancel={() => { setEditingContact(false); setTimeout(() => document.querySelector('[data-testid="crm-contact-edit-btn"]')?.focus(), 0); }}
                    onSaved={async () => { setEditingContact(false); await load(); setTimeout(() => document.querySelector('[data-testid="crm-contact-edit-btn"]')?.focus(), 0); }} />
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                      <div>
                        <Row label="First Name" value={c.first_name} />
                        <Row label="Last Name" value={c.last_name} />
                        <Row label="Business Name" value={c.business_name} />
                        <Row label="Email" value={c.email} />
                        <Row label="Secondary Email" value={c.secondary_email} />
                      </div>
                      <div>
                        <Row label="Mobile Phone" value={c.mobile_phone} />
                        <Row label="Home Phone" value={c.home_phone} />
                        <Row label="WhatsApp" value={c.whatsapp} />
                        <Row label="LINE" value={c.line_id} />
                        <Row label="Website" value={c.website} />
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="crm-contact-mailing-address">
                      <div className="text-sm text-slate-500 mb-1">Mailing Address</div>
                      {c.formatted_mailing_address ? (
                        <div className="text-sm text-slate-800 font-medium whitespace-pre-line break-words">{c.formatted_mailing_address}</div>
                      ) : <div className="text-sm text-slate-400 italic">Not set</div>}
                      {c.address_migration_status === "needs_review" && c.formatted_mailing_address && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-600" data-testid="crm-contact-address-review-cue">
                          <Icons.Info className="h-3 w-3" aria-hidden="true" /> Address imported from a legacy record. Review when convenient.
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mt-3 pt-3 border-t border-slate-100">
                      <div><Row label="Country of Residence" value={c.country_of_residence} /><Row label="Planned / Intended Residence" value={c.planned_country_of_residence} /><Row label="Time Zone" value={c.time_zone} /></div>
                      <div><Row label="Preferred Language" value={c.preferred_language} /><Row label="Citizenship(s)" value={(c.citizenships && c.citizenships.length) ? <CitizenshipBadges value={c.citizenships} testid="crm-contact-citizenships" /> : c.citizenship} /></div>
                    </div>
                  </>
                )}
              </SectionCard>
            </TabsContent>

            {/* RELATIONSHIPS — CRM is the authoritative relationship screen */}
            <TabsContent value="relationships">
              <SectionCard title="Relationships" icon="Network">
                <p className="text-xs text-slate-400 mb-3">The CRM Record is the authoritative home for relationship information. Richer household &amp; entity intelligence is planned for CRM Record 2.1.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <div>
                    <Row label="Spouse / Partner" value={c.spouse_name} />
                    <Row label="Dependents" value={tax.dependent_count ?? null} />
                    <Row label="Household" value={c.household_name} />
                    <Row label="Business Relationships" value={c.business_name} />
                  </div>
                  <div>
                    <Row label="Referred By" value={c.referral_source} />
                    <Row label="Introduced By" value={c.introduced_by} />
                    <Row label="Lead Source" value={c.lead_source} />
                    <Row label="Marketing Campaign" value={c.marketing_campaign} />
                  </div>
                </div>
              </SectionCard>
            </TabsContent>

            {/* TAX PROFILE — read-only summary; editing lives in the Workspace */}
            <TabsContent value="tax">
              <TaxProfileSummaryCard clientId={id} />
              <div className="mt-4">
                <ActionButton icon="LayoutDashboard" variant="secondary" onClick={() => openWs("tax")} data-testid="crm-tax-open-workspace">Review Tax Profile in Workspace</ActionButton>
              </div>
            </TabsContent>

            {/* ASSIGNMENT */}
            <TabsContent value="assignment">
              <SectionCard title="Assignment & Ownership" icon="Users">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  <div><Row label="Assigned Team" value={c.assigned_team_name} /><Row label="Assigned Individual" value={c.assigned_case_manager_name} /><Row label="Priority Level" value={<span className="capitalize">{c.priority_level}</span>} /></div>
                  <div><Row label="Lead Source" value={c.lead_source} /><Row label="Referral Source" value={c.referral_source} /><Row label="Marketing Campaign" value={c.marketing_campaign} /></div>
                </div>
              </SectionCard>
            </TabsContent>

            {/* LIFECYCLE — administrative transitions */}
            <TabsContent value="lifecycle">
              <SectionCard title="Lifecycle Management" icon="GitBranch">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-slate-500">Current lifecycle:</span>
                  <StatusBadge status={c.lifecycle_status} map={LIFECYCLE_TONES} />
                </div>

                {/* Lead: inline Convert to Client with Active / Inactive choice */}
                {c.lifecycle_status === "lead" && (
                  <div className="space-y-3" data-testid="lc-convert-to-client-section">
                    <div className="text-sm font-medium text-slate-700">Convert to Client</div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setConvertTarget(convertTarget === "active_client" ? null : "active_client")}
                        data-testid="lc-choose-active"
                        className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-brand-gold/50 ${
                          convertTarget === "active_client"
                            ? "border-charcoal bg-charcoal text-white"
                            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:border-brand-gold"
                        }`}
                      >
                        <Icons.UserCheck className="h-4 w-4" />
                        Active Client
                      </button>
                      <button
                        onClick={() => setConvertTarget(convertTarget === "inactive_client" ? null : "inactive_client")}
                        data-testid="lc-choose-inactive"
                        className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-brand-gold/50 ${
                          convertTarget === "inactive_client"
                            ? "border-slate-600 bg-slate-700 text-white"
                            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:border-brand-gold"
                        }`}
                      >
                        <Icons.UserMinus className="h-4 w-4" />
                        Inactive Client
                      </button>
                    </div>
                    {convertTarget === "inactive_client" && (
                      <p className="text-xs text-slate-500 flex items-start gap-1.5" data-testid="lc-inactive-hint">
                        <Icons.Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" />
                        Use for a former client who does not currently have an active engagement.
                      </p>
                    )}
                    {convertTarget && (
                      <ActionButton
                        loading={converting}
                        onClick={() => convert(convertTarget)}
                        data-testid="lc-confirm-convert"
                      >
                        Confirm Conversion to {convertTarget === "active_client" ? "Active Client" : "Inactive Client"}
                      </ActionButton>
                    )}
                  </div>
                )}

                {/* Active Client: move to inactive */}
                {c.lifecycle_status === "active_client" && (
                  <ActionButton icon="UserX" variant="secondary" onClick={() => convert("inactive_client")} data-testid="lc-deactivate">
                    Move to Inactive
                  </ActionButton>
                )}

                {/* Inactive Client: reactivate */}
                {c.lifecycle_status === "inactive_client" && (
                  <ActionButton icon="UserCheck" onClick={() => convert("active_client")} data-testid="lc-reactivate">
                    Reactivate
                  </ActionButton>
                )}

                {/* Historical-origin indicator — only shown when reliably persisted provenance exists */}
                {(c.created_via === "uc4_identity_review" || c.lead_source === "historical_email_uc4") && (
                  <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="lc-historical-origin">
                    <Icons.History className="h-3.5 w-3.5" />
                    Created via historical communications review
                  </div>
                )}

                <p className="text-[11px] text-slate-400 mt-3">Lifecycle transitions are administrative and owned by the CRM. Workflow stage advancement lives in the Lead Workspace.</p>

                {/* Permanent Lead Deletion — Administrator only, Lead lifecycle only */}
                {c.lifecycle_status === "lead" && user?.role === "administrator" && (
                  <div className="mt-5 pt-4 border-t border-red-100" data-testid="lead-delete-section">
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">Danger Zone</p>
                    <button
                      onClick={() => setShowLeadDelete(true)}
                      className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 hover:border-red-300 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300"
                      data-testid="lead-delete-open-btn"
                    >
                      <Icons.Trash2 className="h-4 w-4" />
                      Delete Lead Permanently
                    </button>
                    <p className="text-[11px] text-slate-400 mt-1.5">
                      Permanently removes all Lead data. Blocked if financial records exist. Irreversible.
                    </p>
                  </div>
                )}
              </SectionCard>
            </TabsContent>

            {/* PORTAL — administration */}
            <TabsContent value="portal">
              <PortalAdminCard contactId={id} contactEmail={c.email} isAdmin={["administrator", "partner"].includes(user?.role)} />
            </TabsContent>

            {/* FINANCIAL — administration */}
            <TabsContent value="financial">
              <div className="space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MiniStat label="Open Engagements" value={sum.open_engagements} icon="Briefcase" />
                  <MiniStat label="Documents" value={docCount} icon="FileText" />
                  <MiniStat label="Invoices" value={(related?.invoices || []).length} icon="Receipt" />
                  <MiniStat label="Balance" value={usd(sum.outstanding_balance)} icon="CreditCard" tone={sum.outstanding_balance > 0 ? "rose" : "slate"} />
                </div>
                <ListCard title="Invoices" icon="Receipt" rows={related?.invoices} empty="No invoices yet"
                  columns={[["invoice_number", "Number"], ["status", "Status"], ["total", "Total"], ["balance_due", "Balance"]]} money={["total", "balance_due"]} />
                <ListCard title="Payments" icon="CreditCard" rows={related?.payments} empty="No payments yet"
                  columns={[["payment_number", "Number"], ["method", "Method"], ["amount", "Amount"], ["received_at", "Received"]]} money={["amount"]} />
              </div>
            </TabsContent>

            {/* TIMELINE — record history */}
            <TabsContent value="timeline">
              <SectionCard title="Timeline" icon="Activity">
                {timeline.length === 0 ? <EmptyState icon="Activity" title="No activity yet" /> : (
                  <div className="space-y-5">
                    {timeline.map((e, i, arr) => (
                      <TimelineItem key={e.id} icon={e.icon} title={e.title} description={e.description}
                        meta={`${e.actor_name} · ${new Date(e.created_at).toLocaleString()}`} last={i === arr.length - 1} />
                    ))}
                  </div>
                )}
              </SectionCard>
            </TabsContent>

            {/* NOTES — administrative */}
            <TabsContent value="notes">
              <SectionCard title="Notes" icon="StickyNote" action={<ActionButton icon="Plus" onClick={() => setNoteOpen(true)} data-testid="notes-add">Add Note</ActionButton>}>
                {(related?.notes || []).length === 0 ? <EmptyState icon="StickyNote" title="No notes yet" /> : (
                  <div className="space-y-3">
                    {related.notes.map((n) => (
                      <div key={n.id} className="border border-slate-100 rounded-lg p-3" data-testid={`note-${n.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-400">{n.author_name} · {new Date(n.created_at).toLocaleString()}</span>
                          {n.pinned && <Pill tone="warning">Pinned</Pill>}
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <NoteDialog open={noteOpen} onOpenChange={setNoteOpen} clientId={id} onSaved={load} />

      {showLeadDelete && c && (
        <LeadDeletionModal
          clientId={id}
          clientNumber={c.client_number}
          clientName={c.full_name || c.client_number}
          onClose={() => setShowLeadDelete(false)}
          onSuccess={() => navigate("/crm")}
        />
      )}

      {/* Convert to Inactive Client confirmation */}
      <Dialog open={showInactiveConfirm} onOpenChange={setShowInactiveConfirm}>
        <DialogContent data-testid="convert-inactive-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Convert to Inactive Client?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 leading-relaxed">
            Use this for a past customer who has previously purchased services from the firm but does not currently have active work.
          </p>
          <DialogFooter className="gap-2 pt-2">
            <ActionButton variant="secondary" onClick={() => setShowInactiveConfirm(false)} data-testid="convert-inactive-cancel">
              Cancel
            </ActionButton>
            <ActionButton loading={convertingInactive} onClick={convertToInactiveClient} data-testid="convert-inactive-confirm">
              Convert to Inactive Client
            </ActionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge contact dialog */}
      <MergeContactsDialog
        open={showMerge}
        onOpenChange={setShowMerge}
        initialContact={c ? {
          id: c.id, client_number: c.client_number, full_name: c.full_name,
          first_name: c.first_name, last_name: c.last_name,
          lifecycle_status: c.lifecycle_status, email: c.email,
          mobile_phone: c.mobile_phone, phone: c.phone, portal_status: c.portal_status,
        } : null}
        onMerged={(survivorId) => { navigate(`/crm/${survivorId}`); }}
      />
    </div>
  );
}

function NoteDialog({ open, onOpenChange, clientId, onSaved }) {
  const [body, setBody] = useState(""); const [pinned, setPinned] = useState(false); const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setBody(""); setPinned(false); } }, [open]);
  const save = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try { await api.post(`/crm/contacts/${clientId}/notes`, { body, pinned }); toast.success("Note added"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error("Could not save note"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="note-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add Note</DialogTitle></DialogHeader>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Write a note…" data-testid="note-body"
          className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="note-pinned" /> Pin this note</label>
        <DialogFooter className="gap-2">
          <ActionButton variant="secondary" onClick={() => onOpenChange(false)}>Cancel</ActionButton>
          <ActionButton loading={saving} onClick={save} data-testid="note-save">Save Note</ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
